import { useMemo, useState } from "react";
import type { PostDto } from "@codestack/shared";
import { POST_CATEGORIES, POST_STATUSES } from "@codestack/shared";
import { Badge, Button, Input, Modal, Select, StatusBadge, Textarea } from "@codestack/ui";
import { apiRequest, ApiError } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { EmptyState, ErrorState, InlineAlert, LoadingState } from "../components/States";
import { dateTime, relativeTime, titleCase } from "../lib/format";

type FormState = {
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  category: (typeof POST_CATEGORIES)[number];
  status: (typeof POST_STATUSES)[number];
  pinned: boolean;
};

const emptyForm: FormState = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  category: "UPDATE",
  status: "DRAFT",
  pinned: false,
};

const CATEGORY_TONE: Record<string, "neutral" | "info" | "success" | "warning"> = {
  UPDATE: "info",
  RELEASE: "success",
  NEWS: "neutral",
  MAINTENANCE: "warning",
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 160);
}

export function UpdatesPage() {
  const { can } = useAuth();
  const posts = useApi<PostDto[]>("/admin/posts", { limit: 100 });

  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PostDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const canManage = can("content:manage");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (posts.data ?? []).filter((post) => {
      if (statusFilter && post.status !== statusFilter) return false;
      if (!term) return true;
      return (
        post.title.toLowerCase().includes(term) ||
        post.slug.toLowerCase().includes(term) ||
        (post.excerpt ?? "").toLowerCase().includes(term)
      );
    });
  }, [posts.data, statusFilter, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFieldErrors({});
    setFormError(null);
    setEditorOpen(true);
  };

  const openEdit = (post: PostDto) => {
    setEditing(post);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt ?? "",
      body: post.body,
      category: post.category,
      status: post.status,
      pinned: post.pinned,
    });
    setFieldErrors({});
    setFormError(null);
    setEditorOpen(true);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setFieldErrors({});
    setFormError(null);

    const payload = {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      excerpt: form.excerpt.trim() || undefined,
      body: form.body,
      category: form.category,
      status: form.status,
      pinned: form.pinned,
    };

    try {
      if (editing) {
        await apiRequest<PostDto>(`/admin/posts/${editing.id}`, {
          method: "PATCH",
          body: payload,
        });
      } else {
        await apiRequest<PostDto>("/admin/posts", { method: "POST", body: payload });
      }
      setEditorOpen(false);
      posts.reload();
    } catch (cause) {
      if (cause instanceof ApiError) {
        setFormError(cause.message);
        const details = cause.details as
          | { fieldErrors?: Record<string, string[]> }
          | undefined;
        if (details?.fieldErrors) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(details.fieldErrors).map(([key, value]) => [key, value[0] ?? ""]),
            ),
          );
        }
      } else {
        setFormError("Could not save this update.");
      }
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (post: PostDto) => {
    setBusyId(post.id);
    try {
      await apiRequest<PostDto>(`/admin/posts/${post.id}/publish`, {
        method: "POST",
        body: { publish: post.status !== "PUBLISHED" },
      });
      posts.reload();
    } catch (cause) {
      setFormError(cause instanceof ApiError ? cause.message : "Could not change status.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (post: PostDto) => {
    if (!window.confirm(`Delete “${post.title}”? This cannot be undone.`)) return;
    setBusyId(post.id);
    try {
      await apiRequest<void>(`/admin/posts/${post.id}`, { method: "DELETE" });
      posts.reload();
    } catch (cause) {
      setFormError(cause instanceof ApiError ? cause.message : "Could not delete this update.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="admin-card grid gap-3 p-4 sm:grid-cols-[1fr_200px_auto]">
        <Input
          placeholder="Search updates…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="">All statuses</option>
          {POST_STATUSES.map((status) => (
            <option key={status} value={status}>
              {titleCase(status)}
            </option>
          ))}
        </Select>
        {canManage ? (
          <Button onClick={openCreate}>New update</Button>
        ) : (
          <span className="self-center text-xs text-ink-500">Read only</span>
        )}
      </div>

      {formError && !editorOpen ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}

      {posts.loading && !posts.data ? <LoadingState label="Loading updates…" /> : null}

      {posts.error ? (
        <ErrorState message={posts.error} onRetry={posts.reload} />
      ) : null}

      {posts.data && filtered.length === 0 ? (
        <EmptyState
          title="No updates yet"
          description={
            posts.data.length === 0 && canManage
              ? "Write your first update to announce a release or share news on the site."
              : "No updates match the current filters."
          }
          action={posts.data.length === 0 && canManage ? <Button onClick={openCreate}>New update</Button> : undefined}
        />
      ) : null}

      {filtered.length > 0 ? (
        <div className="table-shell animate-in">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="table-head">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Published</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {filtered.map((post) => (
                  <tr key={post.id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ink-100">{post.title}</span>
                        {post.pinned ? <Badge tone="warning">Pinned</Badge> : null}
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] text-ink-500">/{post.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={CATEGORY_TONE[post.category] ?? "neutral"}>
                        {titleCase(post.category)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={post.status} />
                    </td>
                    <td className="px-4 py-3 text-ink-400">
                      {post.publishedAt ? (
                        <span title={dateTime(post.publishedAt)}>
                          {relativeTime(post.publishedAt)}
                        </span>
                      ) : (
                        <span className="text-ink-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canManage ? (
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => togglePublish(post)}
                            disabled={busyId === post.id}
                          >
                            {post.status === "PUBLISHED" ? "Unpublish" : "Publish"}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(post)}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(post)}
                            disabled={busyId === post.id}
                          >
                            Delete
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-ink-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editing ? "Edit update" : "New update"}
        description="Published updates appear on the public site immediately."
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="post-form" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create update"}
            </Button>
          </>
        }
      >
        <form id="post-form" onSubmit={submit} className="space-y-4">
          {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}

          <Input
            label="Title"
            value={form.title}
            onChange={(event) => {
              const title = event.target.value;
              setForm((current) => ({
                ...current,
                title,
                // Keep the slug in step with the title until it is edited by hand.
                slug:
                  current.slug === "" || current.slug === slugify(current.title)
                    ? slugify(title)
                    : current.slug,
              }));
            }}
            error={fieldErrors.title}
            required
          />

          <Input
            label="Slug"
            hint="Leave blank to generate from the title. Changing it changes the public URL."
            value={form.slug}
            onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
            error={fieldErrors.slug}
          />

          <div>
            <Textarea
              label="Excerpt"
              rows={2}
              value={form.excerpt}
              onChange={(event) =>
                setForm((current) => ({ ...current, excerpt: event.target.value }))
              }
              error={fieldErrors.excerpt}
            />
            <p className="mt-1 text-xs text-ink-500">Short summary shown in the updates list.</p>
          </div>

          <div>
            <Textarea
              label="Body"
              rows={12}
              value={form.body}
              onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))}
              error={fieldErrors.body}
              required
            />
            <p className="mt-1 text-xs text-ink-500">
              Plain text. Blank lines separate paragraphs.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Category"
              value={form.category}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  category: event.target.value as FormState["category"],
                }))
              }
            >
              {POST_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {titleCase(category)}
                </option>
              ))}
            </Select>

            <Select
              label="Status"
              value={form.status}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  status: event.target.value as FormState["status"],
                }))
              }
            >
              {POST_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {titleCase(status)}
                </option>
              ))}
            </Select>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink-300">
            <input
              type="checkbox"
              checked={form.pinned}
              onChange={(event) =>
                setForm((current) => ({ ...current, pinned: event.target.checked }))
              }
              className="h-4 w-4 rounded border-white/20 bg-transparent"
            />
            Pin to the top of the updates list
          </label>
        </form>
      </Modal>
    </div>
  );
}
